import React, {FC} from 'react';
import styled from 'styled-components';

import {IconBox} from '../../../../../../../../src/components/common/iconBox/iconBox';
import {Trans} from '../../../../../../../shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {InlineLinkButton} from '../../../../../../../shared/ui/src/components/buttons/inlineLinkButton';
import {Icon} from '../../../../../../../shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../../../shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../../../shared/ui/src/theme/userThemeProvider';

/*
 * Props.
 */

interface ImportCsvHelpBannerProps {
  type: ImportCsvHelpBannerTypesEnum;
  CSVDownloadUrl?: string;
  learnMoreUrl?: string;
  onRequestClose: Handler;
  navigateToLink: HandlerOf<string>;
}

export enum ImportCsvHelpBannerTypesEnum {
  ACCOUNTS = 'accounts',
  CONTACTS = 'contacts',
  DATA_FIELDS_TABLE = 'data_fields_table',
  CIRCLES_TEAMMATES = 'circles_teammates',
}

/*
 * Style.
 */

const StyledIconBox = styled(IconBox)`
  margin-bottom: 20px;
`;

const StyledIconBoxContentDiv = styled.div`
  display: flex;
  flex-flow: column;
  padding-top: 1px;
`;

const StyledTitleDiv = styled.div`
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.small};
`;

const StyledHelpListUl = styled.ul`
  margin-left: -28px;
  padding-left: 21px;
  margin-top: 8px;
  margin-bottom: 16px;
`;

const StyledHelpListItemLi = styled.li`
  font-size: ${fontSizes.verySmall};
  padding-left: 8px;

  &:not(:last-child) {
    margin-bottom: 8px;
  }
`;

const StyledActionsContainerDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 16px;
`;

const StyledButtonContentDiv = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: center;
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.tiny};
  align-items: center;
`;

const StyledButtonIcon = styled(Icon)`
  margin-left: 6px;
`;

/*
 * Component.
 */

export const ImportCSVHelpBanner: FC<ImportCsvHelpBannerProps> = (props) => {
  const theme = useUserTheme();
  const {type, CSVDownloadUrl, learnMoreUrl, onRequestClose, navigateToLink} = props;
  const onCSVTemplateDownloadClick = () => CSVDownloadUrl && navigateToLink(CSVDownloadUrl);

  return (
    <StyledIconBox
      iconName="infoFilledSmall"
      iconSize={16}
      iconColor={theme.palette.blue.shade40}
      borderRadius={16}
      onRequestClose={onRequestClose}
    >
      <StyledIconBoxContentDiv>
        <StyledTitleDiv>
          <Trans id="contacts-manager-csv-import-info-banner-title">Preparing your CSV</Trans>
        </StyledTitleDiv>
        {renderHelpBannerDetails(type)}
        <StyledActionsContainerDiv>
          {learnMoreUrl && (
            <InlineLinkButton onClick={() => navigateToLink(learnMoreUrl)}>
              <StyledButtonContentDiv>
                <Trans id="contacts-manager-csv-import-info-banner-learn-more-link">LEARN MORE</Trans>
                <StyledButtonIcon name="externalLinkSmall" />
              </StyledButtonContentDiv>
            </InlineLinkButton>
          )}
          {CSVDownloadUrl && (
            <InlineLinkButton onClick={onCSVTemplateDownloadClick}>
              <StyledButtonContentDiv>
                <Trans id="contacts-manager-csv-import-info-banner-download-csv-template-link">
                  DOWNLOAD CSV TEMPLATE
                </Trans>
                <StyledButtonIcon name="downloadSmall" />
              </StyledButtonContentDiv>
            </InlineLinkButton>
          )}
        </StyledActionsContainerDiv>
      </StyledIconBoxContentDiv>
    </StyledIconBox>
  );
};

/*
 * Helpers.
 */

function renderHelpBannerDetails(type: ImportCsvHelpBannerTypesEnum) {
  switch (type) {
    case ImportCsvHelpBannerTypesEnum.ACCOUNTS:
      return (
        <StyledHelpListUl>
          <StyledHelpListItemLi>
            <Trans id="contacts-manager-csv-import-info-banner-accounts-supported-properties">
              Supported properties: name (required), description, domains, external ID, custom fields.
            </Trans>
          </StyledHelpListItemLi>
          <StyledHelpListItemLi>
            <Trans id="contacts-manager-csv-import-info-banner-accounts-max-rows">
              You can import a maximum of 3000 rows.
            </Trans>
          </StyledHelpListItemLi>
        </StyledHelpListUl>
      );

    case ImportCsvHelpBannerTypesEnum.CONTACTS:
      return (
        <StyledHelpListUl>
          <StyledHelpListItemLi>
            <Trans id="contacts-manager-csv-import-info-banner-contacts-supported-headings">
              Supported headings: name, email, phone, twitter, link, description, contact list.
            </Trans>
          </StyledHelpListItemLi>
          <StyledHelpListItemLi>
            <Trans id="contacts-manager-csv-import-info-banner-contacts-multiple-entries">
              Append a number to a heading for multiple entries (email2, phone2, etc.).
            </Trans>
          </StyledHelpListItemLi>
          <StyledHelpListItemLi>
            <Trans id="contacts-manager-csv-import-info-banner-contacts-max-rows">
              You can import a maximum of 3000 rows.
            </Trans>
          </StyledHelpListItemLi>
        </StyledHelpListUl>
      );

    case ImportCsvHelpBannerTypesEnum.DATA_FIELDS_TABLE:
      return (
        <Trans id="data-fields-table-csv-upload-preparing-to-import">
          Upload a .CSV file to bulk import data. Files can include a maximum of 500 rows and cells may
          contain numeric or text values.
        </Trans>
      );

    case ImportCsvHelpBannerTypesEnum.CIRCLES_TEAMMATES:
      return (
        <Trans id="circles-teammates-csv-upload-preparing-to-import">
          Upload a .CSV file to bulk add teammates to this teammate group. Files can include a maximum of 1000
          rows. Cells must contain the login email of the teammates to add to the teammate group.
        </Trans>
      );

    default:
      return null;
  }
}
